import type { QueryClient } from "@tanstack/react-query";
import type { GetGroupResponse } from "@/generated";
import { OPEN_BLOCKS_STORAGE_KEY } from "@/store/open-blocks";
import { getActiveGroupId } from "@/utils/activeGroup";

const CACHE_VERSION = 1;
const PREFIX = `noxus:v${CACHE_VERSION}:`;
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

export const CACHE_KEYS = {
  USER: `${PREFIX}user`,
  TENANT: `${PREFIX}tenant`,
  GROUP: `${PREFIX}group`,
  AUTH_USER: `${PREFIX}auth-user`,
  PERMISSIONS: (groupId: string) => `${PREFIX}permissions:${groupId}`,
  CASE_FILTERS: (groupId: string) => `${PREFIX}case-filters:${groupId}`,
} as const;

interface CacheEntry<T> {
  data: T;
  ts: number;
}

export function getCached<T>(key: string): T | undefined {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return undefined;
    const entry = JSON.parse(raw) as CacheEntry<T>;
    if (entry.ts && Date.now() - entry.ts < MAX_AGE_MS) {
      return entry.data;
    }
    // Expired — clean up
    localStorage.removeItem(key);
    return undefined;
  } catch {
    return undefined;
  }
}

export function setCache(key: string, data: unknown): void {
  try {
    const entry: CacheEntry<unknown> = { data, ts: Date.now() };
    localStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // quota exceeded - silently ignore
  }
}

export function clearCache(): void {
  // Clear static keys
  const staticKeys = [
    CACHE_KEYS.USER,
    CACHE_KEYS.TENANT,
    CACHE_KEYS.GROUP,
    CACHE_KEYS.AUTH_USER,
  ];
  staticKeys.forEach((key) => localStorage.removeItem(key));
  // The tools a user had open name them; they go with the user.
  localStorage.removeItem(OPEN_BLOCKS_STORAGE_KEY);

  // Clear all per-group entries (dynamic keys with group suffix). Case
  // filters must not survive logout on a shared machine.
  const permPrefix = `${PREFIX}permissions:`;
  const caseFiltersPrefix = `${PREFIX}case-filters:`;
  Object.keys(localStorage)
    .filter((k) => k.startsWith(permPrefix) || k.startsWith(caseFiltersPrefix))
    .forEach((k) => localStorage.removeItem(k));
}

/**
 * Paint the shell from the last session before the first fetch. Seeded as
 * stale (updatedAt 0) so the shell's staleTime still lets the first mount
 * fetch the real values.
 */
export function seedShellFromStorage(queryClient: QueryClient): void {
  const cachedUser = getCached(CACHE_KEYS.USER);
  if (cachedUser) {
    queryClient.setQueryData(["USER", {}], cachedUser, { updatedAt: 0 });
  }
  const cachedTenant = getCached(CACHE_KEYS.TENANT);
  if (cachedTenant) {
    queryClient.setQueryData(["TENANT", {}], cachedTenant, { updatedAt: 0 });
  }
  // Only the workspace this tab opens: another one's data would send its
  // first requests to the wrong workspace (a 404 on a deep link).
  const cachedGroup = getCached<GetGroupResponse>(CACHE_KEYS.GROUP);
  const groupId = getActiveGroupId();
  if (cachedGroup && groupId && cachedGroup.group.id === groupId) {
    queryClient.setQueryData(
      ["GROUPS", "GROUP", { path: { group_id: groupId } }],
      cachedGroup,
      { updatedAt: 0 }
    );
  }
}
